import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import './EkinextList.css';
import CreateEkinextModal from './CreateEkinextModal';

const EkinextList = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingRows, setEditingRows] = useState({});
  const [showCreateModal, setShowCreateModal] = useState(false);

  function formatDateIT(dateString) {
    if (!dateString) return '-';
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  }

  useEffect(() => {
    loadProducts();
  }, []);

  async function loadProducts() {
    setLoading(true);
    try {
      const res = await api.getEkinextProducts();
      if (res.success) {
        setProducts(res.products || []);
      }
    } catch (err) {
      console.error('Errore caricamento prodotti:', err);
    } finally {
      setLoading(false);
    }
  }

  function handleFieldChange(productId, field, value) {
    setEditingRows(prev => ({
      ...prev,
      [productId]: { ...prev[productId], [field]: value }
    }));
  }

  async function handleSave(productId) {
    const changes = editingRows[productId];
    if (!changes || Object.keys(changes).length === 0) return;

    const product = products.find(p => p.id === productId);
    const updatedProduct = { ...product, ...changes };

    try {
      const res = await api.updateEkinextProduct(updatedProduct);
      if (res.success) {
        await loadProducts();
        setEditingRows(prev => {
          const newState = { ...prev };
          delete newState[productId];
          return newState;
        });
      } else {
        alert('Errore salvataggio: ' + res.error);
      }
    } catch (err) {
      alert('Errore di connessione');
    }
  }

  async function handleDelete(productId) {
    if (!window.confirm('Sei sicuro di voler eliminare questo prodotto?')) return;

    try {
      const res = await api.deleteEkinextProduct(productId);
      if (res.success) {
        await loadProducts();
      } else {
        alert('Errore eliminazione: ' + res.error);
      }
    } catch (err) {
      alert('Errore di connessione');
    }
  }

  function getStatusBadgeClass(status) {
    switch (status) {
      case 'pianificato': return 'badge bg-secondary';
      case 'in_corso': return 'badge bg-primary';
      case 'concluso': return 'badge bg-success';
      case 'abortito': return 'badge bg-danger';
      default: return 'badge bg-secondary';
    }
  }

  function handleBack() {
    navigate('/tools');
  }

  const isAdmin = user?.role === 'admin';

  if (loading) {
    return <div className="loading-container">Caricamento prodotti...</div>;
  }

  return (
    <div className="ekinext-list-page">
      {/* HEADER NAVIGATION */}
      <div className="ekinext-header">
        <button className="btn-back" onClick={handleBack}>
          ← Torna a Tools
        </button>
        <h1>Gestione Prodotti Ekinext</h1>
        <button className="btn-logout" onClick={onLogout}>
          Logout
        </button>
      </div>

      {/* CONTENT */}
      <div className="ekinext-content">
        {isAdmin && (
          <button 
            className="btn-create" 
            onClick={() => setShowCreateModal(true)}
          >
            + Nuovo Prodotto
          </button>
        )}

        <div className="table-responsive">
          <table className="table table-striped events-table">
            <thead>
              <tr>
                <th>Nome Prodotto</th>
                <th>Data Invio Email</th>
                <th>Data Inizio</th>
                <th>Data Primo Feedback</th>
                <th>Data Chiusura</th>
                <th>Stato</th>
                <th>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {products.map(product => {
                const hasChanges = editingRows[product.id] && 
                  Object.keys(editingRows[product.id]).length > 0;

                return (
                  <tr key={product.id}>
                    <td>
                      {isAdmin ? (
                        <input
                          type="text"
                          className="form-control"
                          value={editingRows[product.id]?.product_name ?? product.product_name}
                          onChange={e => handleFieldChange(product.id, 'product_name', e.target.value)}
                        />
                      ) : (
                        product.product_name
                      )}
                    </td>
                    <td>
                      {isAdmin ? (
                        <input
                          type="date"
                          className="form-control"
                          value={editingRows[product.id]?.email_sent_date ?? product.email_sent_date ?? ''}
                          onChange={e => handleFieldChange(product.id, 'email_sent_date', e.target.value)}
                        />
                      ) : (
                        formatDateIT(product.email_sent_date)
                      )}
                    </td>
                    <td>
                      {isAdmin ? (
                        <input
                          type="date"
                          className="form-control"
                          value={editingRows[product.id]?.start_date ?? product.start_date ?? ''}
                          onChange={e => handleFieldChange(product.id, 'start_date', e.target.value)}
                        />
                      ) : (
                        formatDateIT(product.start_date)
                      )}
                    </td>
                    <td>
                      {isAdmin ? (
                        <input
                          type="date"
                          className="form-control"
                          value={editingRows[product.id]?.first_feedback_date ?? product.first_feedback_date ?? ''}
                          onChange={e => handleFieldChange(product.id, 'first_feedback_date', e.target.value)}
                        />
                      ) : (
                        formatDateIT(product.first_feedback_date)
                      )}
                    </td>
                    <td>
                      {isAdmin ? (
                        <input
                          type="date"
                          className="form-control"
                          value={editingRows[product.id]?.close_date ?? product.close_date ?? ''}
                          onChange={e => handleFieldChange(product.id, 'close_date', e.target.value)}
                        />
                      ) : (
                        formatDateIT(product.close_date)
                      )}
                    </td>
                    <td>
                      {isAdmin ? (
                        <select
                          className="form-select"
                          value={editingRows[product.id]?.status ?? product.status}
                          onChange={e => handleFieldChange(product.id, 'status', e.target.value)}
                        >
                          <option value="pianificato">Pianificato</option>
                          <option value="in_corso">In Corso</option>
                          <option value="concluso">Concluso</option>
                          <option value="abortito">Abortito</option>
                        </select>
                      ) : (
                        <span className={getStatusBadgeClass(product.status)}>
                          {product.status}
                        </span>
                      )}
                    </td>
                    <td>
                      {hasChanges && isAdmin && (
                        <button 
                          className="btn btn-sm btn-success me-2"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSave(product.id);
                          }}
                        >
                          Salva
                        </button>
                      )}
                      {isAdmin && (
                        <button 
                          className="btn btn-sm btn-danger me-2"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(product.id);
                          }}
                        >
                          Elimina
                        </button>
                      )}
                      <button 
                        className="btn-view"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/ekinext/${product.id}`);
                        }}
                      >
                        Dettagli
                      </button>
                    </td>

                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showCreateModal && (
        <CreateEkinextModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            loadProducts();
          }}
        />
      )}
    </div>
  );
};

export default EkinextList;
